import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, type DayPickerProps } from 'react-day-picker';
import { es } from 'react-day-picker/locale';
import { cn } from '../../lib/cn.js';

// shadcn-style Calendar on react-day-picker, themed with Convometry tokens.
// Dates are calendar days (YYYY-MM-DD) interpreted in the business timezone; helpers below convert
// between those strings and browser-local Date objects at midnight, so no timezone shift occurs.

export function parseLocalDay(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function toLocalDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export type CalendarProps = DayPickerProps;

export function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      locale={es}
      showOutsideDays={showOutsideDays}
      className={cn('p-3 text-body', className)}
      classNames={{
        months: 'relative flex flex-col gap-4 sm:flex-row',
        month: 'flex flex-col gap-3',
        month_caption: 'flex h-9 items-center justify-center',
        caption_label: 'text-body font-semibold capitalize text-text-primary',
        nav: 'absolute inset-x-0 top-0 flex h-9 items-center justify-between',
        button_previous: navButton,
        button_next: navButton,
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'w-9 text-helper font-medium capitalize text-text-secondary',
        week: 'mt-1 flex w-full',
        day: 'relative size-9 p-0 text-center',
        day_button: cn(
          'cm-tabular inline-flex size-9 items-center justify-center rounded-control text-body text-text-primary',
          'transition-colors duration-[var(--cm-motion-fast)] hover:bg-surface-subtle',
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
        ),
        selected: '[&>button]:bg-brand-primary [&>button]:text-brand-on-primary [&>button]:hover:bg-brand-hover',
        range_start: 'rounded-l-control bg-brand-tint',
        range_end: 'rounded-r-control bg-brand-tint',
        range_middle:
          'bg-brand-tint first:rounded-l-control last:rounded-r-control [&>button]:bg-transparent! [&>button]:text-text-primary! [&>button]:hover:bg-surface-default!',
        today: '[&>button]:font-semibold [&>button]:underline [&>button]:underline-offset-4',
        outside: '[&>button]:text-text-muted',
        disabled: '[&>button]:cursor-not-allowed [&>button]:opacity-40',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: c }) =>
          orientation === 'left' ? (
            <ChevronLeft aria-hidden className={cn('size-4', c)} />
          ) : (
            <ChevronRight aria-hidden className={cn('size-4', c)} />
          ),
      }}
      {...props}
    />
  );
}

const navButton = cn(
  'inline-flex size-8 items-center justify-center rounded-control border border-border-control text-text-secondary hover:border-border-control-strong',
  'hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
  'disabled:opacity-40',
);
